/**
 * @file Projects.jsx
 * @description Projects gallery page with Tailwind CSS
 */

import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import IMAGES from '../config/images';

const Projects = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);

  const categories = [
    { id: 'all', label: 'All Projects' },
    { id: 'plumbing', label: 'Plumbing' },
    { id: 'gas', label: 'Gas' },
    { id: 'heating', label: 'Heating' },
    { id: 'emergency', label: 'Emergency' }
  ];

  const projects = [
    {
      id: 1,
      title: 'Complete Bathroom Renovation',
      category: 'plumbing',
      image: IMAGES.projectBathroom,
      description: 'Full bathroom suite installation with modern fixtures',
      details: 'Complete bathroom renovation including new suite, tiling, and plumbing installation in North London home.',
      location: 'North London'
    },
    {
      id: 2,
      title: 'New Boiler Installation',
      category: 'heating',
      image: IMAGES.projectBoiler,
      description: 'Worcester Bosch combi boiler replacement',
      details: 'Replacement of old conventional boiler with new energy-efficient Worcester Bosch combi boiler.',
      location: 'Central London'
    },
    {
      id: 3,
      title: 'Gas Safety Inspection',
      category: 'gas',
      image: IMAGES.projectGasInspection,
      description: 'Comprehensive gas safety certificate',
      details: 'Full gas safety inspection and certification for landlord property with multiple gas appliances.',
      location: 'East London'
    },
    {
      id: 4,
      title: 'Emergency Leak Repair',
      category: 'emergency',
      image: IMAGES.projectEmergency,
      description: '24-hour emergency burst pipe repair',
      details: 'Emergency callout for burst pipe repair preventing water damage to property.',
      location: 'West London'
    },
    {
      id: 5,
      title: 'Central Heating System',
      category: 'heating',
      image: IMAGES.projectCentralHeating,
      description: 'Complete central heating installation',
      details: 'Full central heating system installation with new radiators throughout 3-bedroom house.',
      location: 'South London'
    },
    {
      id: 6,
      title: 'Kitchen Plumbing',
      category: 'plumbing',
      image: IMAGES.projectKitchen,
      description: 'Kitchen sink and appliance installations',
      details: 'Installation of new kitchen sink, dishwasher, and washing machine plumbing.',
      location: 'North London'
    },
    {
      id: 7,
      title: 'Gas Cooker Installation',
      category: 'gas',
      image: IMAGES.projectGasCooker,
      description: 'Gas cooker fitting and certification',
      details: 'Professional gas cooker installation with safety testing and certification.',
      location: 'Central London'
    },
    {
      id: 8,
      title: 'Radiator Replacement',
      category: 'heating',
      image: IMAGES.projectRadiator,
      description: 'Modern radiator upgrades',
      details: 'Replacement of old radiators with modern energy-efficient models throughout property.',
      location: 'East London'
    },
    {
      id: 9,
      title: 'Drain Unblocking',
      category: 'emergency',
      image: IMAGES.projectDrain,
      description: 'Emergency drain clearance',
      details: 'Emergency drain unblocking service preventing flooding in commercial property.',
      location: 'West London'
    }
  ];

  const filteredProjects = activeFilter === 'all' 
    ? projects 
    : projects.filter(p => p.category === activeFilter);

  return (
    <div className="projects-page">
      <Helmet>
        <title>Projects | Metro Plumbing & Heating</title>
        <meta
          name="description"
          content="View our completed plumbing, gas, and heating projects across London. See examples of our quality workmanship."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-900 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute w-96 h-96 bg-white rounded-full top-0 right-0 animate-pulse-slow"></div>
          <div className="absolute w-64 h-64 bg-white rounded-full bottom-0 left-0 animate-bounce-slow"></div>
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto animate-fade-in-up">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Our Projects</h1>
            <p className="text-xl text-blue-100">
              Explore our completed projects across London
            </p>
          </div>
        </div>
      </section>

      {/* Filter Buttons */}
      <section className="py-8 bg-gray-50  z-40 shadow-md">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveFilter(cat.id)}
                className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 transform hover:-translate-y-1 hover:scale-110 relative overflow-hidden ${
                  activeFilter === cat.id
                    ? 'bg-primary-600 text-white shadow-lg scale-105'
                    : 'bg-white text-gray-700 hover:bg-primary-50 shadow'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project, index) => (
              <div
                key={project.id}
                className="card group cursor-pointer overflow-hidden animate-fade-in-up"
                style={{ animationDelay: `${index * 0.05}s` }}
                onClick={() => setSelectedProject(project)}
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                    onError={(e) => {
                      e.target.src = IMAGES.projectPlaceholder;
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 bg-primary-600 text-white text-sm font-semibold rounded-full capitalize shadow-lg animate-bounce-slow">
                      {project.category}
                    </span>
                  </div>
                  {/* Decorative corner */}
                  <div className="absolute bottom-0 left-0 w-20 h-20 bg-gradient-to-tr from-black/20 to-transparent rounded-tr-full"></div>
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="bg-white rounded-full p-4 transform scale-0 group-hover:scale-100 transition-transform duration-300">
                      <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                      </svg>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors duration-300">
                    {project.title}
                  </h3>
                  <p className="text-gray-600 mb-3">{project.description}</p>
                  <div className="flex items-center text-sm text-gray-500">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    {project.location}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Modal */}
      {selectedProject && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setSelectedProject(null)}
        >
          <div
            className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <img
                src={selectedProject.image}
                alt={selectedProject.title}
                className="w-full h-96 object-cover rounded-t-2xl"
                onError={(e) => {
                  e.target.src = '/images/project-placeholder.jpg';
                }}
              />
              <button
                onClick={() => setSelectedProject(null)}
                className="absolute top-4 right-4 bg-white rounded-full p-2 shadow-lg hover:shadow-xl transform hover:scale-110 transition-all duration-300"
              >
                <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div className="absolute top-4 left-4">
                <span className="px-4 py-2 bg-primary-600 text-white font-semibold rounded-full capitalize">
                  {selectedProject.category}
                </span>
              </div>
            </div>
            <div className="p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">{selectedProject.title}</h2>
              <div className="flex items-center text-gray-600 mb-6">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                {selectedProject.location}
              </div>
              <p className="text-lg text-gray-700 leading-relaxed">{selectedProject.details}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Projects;
